import type { CueItemPhase } from '@/types/player/perform-control-v1.types';

export const CUE_TRANSPORT_BUSY_WATCHDOG_MS = 2_000;

export type CueTransportBusyLatch = {
  active: boolean;
  latchedAtMs: number;
  preClickPointer: number;
  preClickSeq: number;
};

export function createCueTransportBusyLatch(
  pointer: number,
  seq: number,
  nowMs: number = Date.now(),
): CueTransportBusyLatch {
  return {
    active: true,
    latchedAtMs: nowMs,
    preClickPointer: pointer,
    preClickSeq: seq,
  };
}

/**
 * Transport busy for Next Play / prev-next: latch on click; clear on on_air progress,
 * mutation error, or HTTP settle. Watchdog only triggers a refetch — it does not re-enable
 * Play (avoids multi-advance while a take is still running).
 */
export function resolveCueTransportBusy(params: {
  latch: CueTransportBusyLatch | null | undefined;
  pointer: number | undefined;
  currentPhase: CueItemPhase | undefined;
  seq: number;
  mutationFailed?: boolean;
  /** Cue-command HTTP finished (success). */
  mutationSettled?: boolean;
  nowMs?: number;
  watchdogMs?: number;
}): { busy: boolean; clearLatch: boolean; watchdogFired: boolean } {
  const latch = params.latch;
  if (!latch?.active) {
    return { busy: false, clearLatch: false, watchdogFired: false };
  }

  if (params.mutationFailed) {
    return { busy: false, clearLatch: true, watchdogFired: false };
  }

  const pointer = params.pointer;
  const pointerMoved = pointer != null && pointer !== latch.preClickPointer;
  const seqAdvanced = params.seq > latch.preClickSeq;
  const onAir = params.currentPhase === 'on_air';
  if (onAir && (pointerMoved || seqAdvanced)) {
    return { busy: false, clearLatch: true, watchdogFired: false };
  }

  if (params.mutationSettled) {
    return { busy: false, clearLatch: true, watchdogFired: false };
  }

  const nowMs = params.nowMs ?? Date.now();
  const watchdogMs = params.watchdogMs ?? CUE_TRANSPORT_BUSY_WATCHDOG_MS;
  if (nowMs - latch.latchedAtMs >= watchdogMs) {
    // Refetch only — keep busy so rapid re-clicks cannot enqueue another nextMedia.
    return { busy: true, clearLatch: false, watchdogFired: true };
  }

  return { busy: true, clearLatch: false, watchdogFired: false };
}
